import { produce } from 'immer';
import { entity } from 'simpler-state';

import entityReduxLogger from '../debug/helpers/entity-redux-logger';
import { OHLCV } from '../models/market-data/types';
import { SmallChartData } from '../store/market-data/charting/types';

import CallsCache from './calls-cache/CallsCache';

export type SmallChartDataMap = Record<string, SmallChartData>;

const SmallChartDataCache = entity<SmallChartDataMap>({}, entityReduxLogger('SmallChart', 'small-chart'));

const EMPTY_SMALL_CHART_DATA: SmallChartData = {
  ohlcvData: [] as OHLCV[],
  closeValues: [] as number[],
  isReady: false,
};

export type SmallChartUpdateOptions = {
  hasNoDataSymbols?: string[];
  dailyMaxPoints?: number;
};

/**
 * Used only by Gateway service to update cache from response messages.
 * @param newData The chart data sent with update
 * @param traceId The trace identifier for the call
 * @param options See `SmallChartUpdateOptions`
 */
export const updateSmallChartDataCache = (
  newData: Record<string, OHLCV[]>,
  traceId: string,
  options: SmallChartUpdateOptions = {},
) => {
  const { hasNoDataSymbols, dailyMaxPoints } = options;

  SmallChartDataCache.set(
    produce(state => {
      /* eslint-disable no-param-reassign */
      Object.entries(newData ?? {}).forEach(([ symbol, data ]) => {
        const hasNoData = hasNoDataSymbols?.includes(symbol) || false;
        if (data || hasNoData) {
          const dataArray = data ?? [];
          state[symbol] = {
            ohlcvData: dataArray,
            closeValues: dataArray.map(({ close }) => close),
            isReady: true,
            dailyMaxPoints,
          };
        }
      });
    }),
  );

  // process CallsCache for smallChart
  CallsCache.processResponseByTraceId(traceId, 'getChartingData_small');
};


// hiding direct setter by not including it in exports
export default {
  get: (symbol: string) => SmallChartDataCache.get()[symbol] ?? EMPTY_SMALL_CHART_DATA,
  getCount: () => Object.keys(SmallChartDataCache.get()).length,
  use: (symbol: string) => SmallChartDataCache.use(data => data?.[symbol] ?? EMPTY_SMALL_CHART_DATA),
  useAll: SmallChartDataCache.use,
};
